import { Injectable } from '@angular/core';

import { BehaviorSubject, finalize, Subscription, Observable, forkJoin, of } from 'rxjs';
import { catchError, tap, map, switchMap } from 'rxjs/operators';
import { partition } from 'lodash';
import moment from 'moment-timezone';
import { MessageService } from 'primeng/api';


import { AgentsApiService } from '../../../api/agents/agents.api';
import { PrecisionClipApi, PrecisionClipResponse } from '../../../api/agents/models/precision-clip.api.model';
import { KeywordSelectionEnum } from '@Monitor/Shared/enums/precision-keyword-selection.enum';
import { Mention } from '../../../Common/Mentions/models/mention';
import { messageSeverityEnum } from '@Common/Misc/enums/message-severity.enum';
import { MentionSearchModel } from '@Monitor/Shared/models/mention-search.model';
import { MentionsApi } from '../../../api/mentions/mentions.api';
import { SaveMentionsRequestModel } from '../../../api/mentions/model/save-mentions-request.model';
import { MentionIdMercuryMapModel } from '../../../api/mentions/model/mention-id-mercury-map.model';
import { SaveMentionsModel } from '../models/save-mentions.model';
import { MentionInsightModel } from '../../../Common/Mentions/models/mention-insight.model';
import { TVEyesPlayerletteUrl } from '../../../Common/Mentions/models/tveyes-playerlette-url.model';
import { TVEyesArchivedStateEnum } from '../../../api/mentions/enum/tv-eyes-archived-state.enum';
import { BriefingsService } from '@Monitor/Briefings/services/briefings.service';


export interface PrecisionConfig {
    keywordSelection: KeywordSelectionEnum;
    manualKeywords: string[];
    fallbackKeywords: string[];
    mentions: Mention[];
    mentionSearch: MentionSearchModel;
    insightsEnabled: boolean;
    extendStartTime: number;
    extendedEndTime: number;
}


export enum PrecisionResourceType {
    BRIEFING = 'BRIEFING',
    ALERT = 'ALERT'
}

type ProcessKey = `${PrecisionResourceType}:${string}`;

export interface PrecisionClipProcess {
    id: number;
    type: PrecisionResourceType;
    status: 'pending' | 'completed' | 'canceled';
    clipsCompleted: number;
    totalClipsToComplete: number;
    clipsFailed: number;
}

interface PrecisionclipOutcome {
    mention: Mention;
    response?: PrecisionClipResponse;
    error?: any;
}


/**
 * Responsible for managing precision clip requests across Briefings and Alerts
 */
@Injectable({ providedIn: 'root' })
export class MonitoringPrecisionClipService {

    private processes = new Map<ProcessKey, BehaviorSubject<PrecisionClipProcess | undefined>>();
    private requestSubs = new Map<ProcessKey, Subscription>();

    constructor(
        private agentApiService: AgentsApiService,
        private mentionApiService: MentionsApi,
        private briefingsService: BriefingsService,
        private messageService: MessageService
    ) {}


    getClipableMentions(payload: PrecisionConfig): Mention[] {
        if (!payload) {
            return [];
        }
        return payload.mentions.filter(mention => {
            if (payload.keywordSelection === KeywordSelectionEnum.SAVED_MENTION) {

                const noContextualKeywords = !mention.contextualKeywords?.length;
                const noMatchedKeywords = !mention.matchedKeywords?.length;
                const noFallbackKeywords = !payload.fallbackKeywords?.length;

                return !(noContextualKeywords && noMatchedKeywords && noFallbackKeywords) && mention.isPrecisionClipable();
            }

            return mention.isPrecisionClipable();
        }) || [];
    }

    /**
     * Start precision clipping process for given resource and config.
     * Fetch the precision clip data and save the mention (to get a mentionId) if not already saved.
     * Patch the mention if insight generation is enabled and save the new playerlett clipped url to the mention.
     */
    start(id: number, type: PrecisionResourceType, payload: PrecisionConfig) {
        const key = this.toKey(id, type);

        const clipableMentions = this.getClipableMentions(payload);

        if (clipableMentions?.length === 0) {

            return;
        }

        const precisionClipProcess: PrecisionClipProcess = {
            id: id,
            type: type,
            status: 'pending',
            clipsCompleted: 0,
            clipsFailed: 0,
            totalClipsToComplete: clipableMentions.length || 0,
        };

        const precisionClipProcess$ = this.getOrCreateProcess(key);

        precisionClipProcess$.next(precisionClipProcess);

        const emitProgress = (patch: Partial<PrecisionClipProcess>) =>
            precisionClipProcess$.next({ ...precisionClipProcess$.value, ...patch });


        const precisionClip$: Observable<PrecisionclipOutcome>[] = clipableMentions.map(mention => {

            let keywords: string[] = [];

            if (payload.keywordSelection === KeywordSelectionEnum.MANUAL) {
                keywords = payload.manualKeywords;
            } else {
                if (mention.contextualKeywords.length) {
                    keywords = mention.contextualKeywords;
                } else if (mention?.matchedKeywords?.length) {
                    keywords = mention?.matchedKeywords;
                } else {
                    keywords = payload.fallbackKeywords ?? [];
                }
            }

            const request: PrecisionClipApi = {
                keywords: keywords,
                playerletteUrl: mention.url,
                startOffsetSeconds: payload.extendStartTime,
                endOffsetSeconds: payload.extendedEndTime
            };

            return this.agentApiService.getPrecisionClip(request).pipe(
                tap(() => emitProgress({ clipsCompleted: precisionClipProcess$.value.clipsCompleted + 1})),
                map(response => ({ mention, response } as PrecisionclipOutcome)),
                catchError(error => {
                    emitProgress({ clipsFailed: precisionClipProcess$.value.clipsFailed + 1 });

                    return of({ mention, error } as PrecisionclipOutcome);
                })
            );
        });


        const sub = forkJoin(precisionClip$).pipe(
            map(reponses => reponses.filter(x => !!x.response)),
            switchMap(successes => {
                const [needSave, haveId] = partition(successes, x => !x.mention.mentionId);

                const bulkSave$ = needSave.length
                    ? this.bulkMentionSave(needSave.map(x => x.mention), payload.mentionSearch) : of(null);

                return bulkSave$.pipe(map(() => successes));
            }),
            switchMap((outcomes: PrecisionclipOutcome[]) => {
                if (!payload.insightsEnabled || !outcomes.length) {
                    return of(outcomes);
                }

                const mentionsArray = outcomes.map(outcome => {
                    const newInsight = new MentionInsightModel();
                    newInsight.name = 'Clip Insights';
                    newInsight.content = `${outcome.response.primary.rationale}<br>Segment Type: ${outcome.response.primary.segmentType}`;
                    newInsight.isPreferred = true;

                    if (!outcome.mention.insights) {
                        outcome.mention.insights = [];
                    }
                    outcome.mention.insights.push(newInsight);

                    return outcome.mention.convertToApiMention();
                });

                return this.mentionApiService.patchMentions(mentionsArray).pipe(
                    map(() => outcomes),
                    catchError(err => {
                        return of([]);
                    })
                );
            }),
            switchMap((outcomes: PrecisionclipOutcome[]) => {
                if (!outcomes.length) {
                    return of([]);
                }

                const requestsArray = outcomes.map(outcome => {
                    const params = new URLSearchParams(outcome.mention.url);
                    const startTime = moment.utc(params.get(TVEyesPlayerletteUrl.START_DATE_TIME));

                    const startDateTime = startTime.clone().add(outcome.response.primary.adjustedStartTimestamp, 'seconds');
                    const existing = TVEyesPlayerletteUrl.fromUrl(outcome.mention.url);

                    const newPlayerletteUrl = new TVEyesPlayerletteUrl(
                        outcome.mention.url,
                        startDateTime,
                        outcome.response.primary.durationSeconds,
                        existing.getStationId(),
                    ).getMentionUrl();

                    return {
                        id: outcome.mention.mentionId,
                        isDirectArchive: false,
                        clipDuration: outcome.response.primary.durationSeconds,
                        body: outcome.mention.body,
                        tveyesArchivedState: TVEyesArchivedStateEnum.EDITED,
                        tvEyesInstaPlayUrl: newPlayerletteUrl,
                        startDateTime: startDateTime.valueOf()
                    };
                });

                return this.mentionApiService.patchTVEyesMentions(requestsArray).pipe(
                    tap(() => {
                        return this.briefingsService.refreshCuratedMentions()
                    }),
                    catchError(err => {
                        return of(null);
                    })
                );
            }),
            finalize(() => this.requestSubs.delete(key)),
        ).subscribe({
            next: () => emitProgress({ status: 'completed' }),
            error: () => {
                this.messageService.add({
                    severity : messageSeverityEnum.error,
                    summary: 'Monitor.precisionClip.error.heading',
                    detail: 'Monitor.precisionClip.error.messaging'
                });
                emitProgress({ status: 'canceled' });
            }
        });


        this.requestSubs.set(key, sub);
    }

    cancel(id: number, type: PrecisionResourceType) {
        const key = this.toKey(id, type);

        // stop listening to / kill the in-flight request
        this.requestSubs.get(key)?.unsubscribe();
        this.requestSubs.delete(key);

        const precisionClipProcess$ = this.processes.get(key);
        precisionClipProcess$.next({ ...precisionClipProcess$.value, status: 'canceled' });
        this.processes.delete(key);
    }


    watch(id: number, type: PrecisionResourceType): Observable<PrecisionClipProcess | undefined> {
        return this.getOrCreateProcess(this.toKey(id, type));
    }


    private getOrCreateProcess(key: ProcessKey): BehaviorSubject<PrecisionClipProcess | undefined> {
        let subject = this.processes.get(key);

        if (!subject) {
            subject = new BehaviorSubject<PrecisionClipProcess | undefined>(undefined);
            this.processes.set(key, subject);
        }

        return subject;
    }


    private toKey(id: number, type: PrecisionResourceType): ProcessKey {
        return `${type}:${id}`;
    }

    /**
     * Bulk save mentions, and update newly created id property to objects
     */
    private bulkMentionSave(mentions: Mention[], search: MentionSearchModel): Observable<MentionIdMercuryMapModel[]> {

        const payload: SaveMentionsRequestModel = new SaveMentionsModel(mentions, search).getSaveNewMentionsRequestModel();

        return this.mentionApiService.createMentionsFromMercury(payload).pipe(
            tap(bulkResposne => {
                mentions.forEach(mention => {
                    const specificResponse = bulkResposne.find(x => x.mercuryId === mention.id);
                    if (specificResponse) {
                        mention.mentionId = specificResponse.mentionId;
                    }
                });
            })
        );
    }
}
